import ko = require("knockout");
import _ = require("underscore");
import Service = require("./Service");

interface IExtendedErrorRegistrationItem {
    id: any;
    error: string;
    friendlyName: string | KnockoutObservable<string>;
}

export class ExtenderRegistration {
    static register() {
        //console.log("registering extenders");
        ko.extenders.numeric = (target, options: any) => {
            //create a writeable computed observable to intercept writes to our observable
            var precision, allowNull, minimumValue, maximumValue;
            precision = 2;
            allowNull = false;
            minimumValue = null;
            maximumValue = null;


            if (options !== undefined && options !== null) {
                if (typeof options === "number") {
                    precision = options;
                } else if (typeof options === "object") {
                    if (options.precision !== undefined && options.precision !== null && typeof options.precision === "number") {
                        precision = options.precision;
                    }
                    if (options.allowNull !== undefined && options.allowNull !== null && typeof options.allowNull === "boolean") {
                        allowNull = options.allowNull;
                    }
                    if (options.minimumValue !== undefined && options.minimumValue !== null && typeof options.minimumValue === "number") {
                        minimumValue = options.minimumValue;
                    }
                    if (options.maximumValue !== undefined && options.maximumValue !== null && typeof options.maximumValue === "number") {
                        maximumValue = options.maximumValue;
                    }
                }
            }

            var result = ko.pureComputed({
                read: target,  //always return the original observables value
                write: (newValue: any) => {
                    var current = target(),
                        roundingMultiplier = Math.pow(10, precision),
                        newValueAsNum = isNaN(newValue) ? 0 : parseFloat(newValue),
                        valueToWrite = Math.round(newValueAsNum * roundingMultiplier) / roundingMultiplier;
                    if (allowNull && isNaN(valueToWrite)) {
                        valueToWrite = null;
                    }
                    if (minimumValue !== null) {
                        if (valueToWrite !== null) {
                            if (valueToWrite < minimumValue) {
                                valueToWrite = minimumValue;
                            }
                        }
                    }
                    if (maximumValue !== null) {
                        if (valueToWrite !== null) {
                            if (valueToWrite > maximumValue) {
                                valueToWrite = maximumValue;
                            }
                        }
                    }

                    //only write if it changed
                    if (valueToWrite !== current) {
                        target(valueToWrite);
                    } else {
                        //if the rounded value is the same, but a different value was written, force a notification for the current field
                        if (newValue !== current) {
                            target.notifySubscribers(valueToWrite);
                        }
                    }
                }
            }).extend({ notify: 'always' });

            //initialize with current value to make sure it is rounded appropriately
            result(target());

            //return the new computed observable
            return result;
        };

        ko.extenders.datetime = (target, dateType?: Service.dateFormatValue | KnockoutObservable<Service.dateFormatValue>) => {
            //create a writable computed observable to intercept writes to our observable
            var result = ko.pureComputed({
                read: () => {
                    var current = target();
                    var type: Service.dateFormatValue = <Service.dateFormatValue>ko.unwrap(dateType) || "datetime";
                    var retVal = Service.coerceDateValue(current, type);
                    console.log(`read of dateTimeExtender.  Modified value "${current}" to "${retVal}" using format type of ${type}.`);
                    return retVal;
                }, 
                write: (newValue) => {
                    console.log(`Received new value of "${newValue}"`);
                    var current = target();
                    var type = <Service.dateFormatValue>ko.unwrap(dateType) || "datetime";

                    var newValueAsDateTime = Service.coerceDateValue(newValue, type, true);
                    var valueToWrite = newValueAsDateTime;
                    console.log(`Writing value of "${valueToWrite}" using coerced type of ${type}`);
                    //only write if it changed
                    if (valueToWrite !== current) {
                        target(valueToWrite);
                    } else {
                        //if the formatted value is the same, but a different value was written, force a notification for the current field
                        if (newValue !== current) {
                            target.notifySubscribers(valueToWrite);
                        }
                    }
                }
            }).extend({ notify: 'always' });
 
            //initialize with current value to make sure it is formatted appropriately
            result(target());

            if (ko.isObservable(dateType)) {
                //force computed to re-evaluate when date type changes
                (<KnockoutObservable<Service.dateFormatValue>>dateType).subscribe(() => {
                    result(result());
                });
            }

            //return the new computed observable
            return result;
        };

        var defaultFormatIdentifier = (title:string) => {
            if (!title) {
                return title;
            }
            return title.toLowerCase().replace(/[^a-zA-Z0-9_-]/g, "_").replace(/_-_/g, "_");
        };

        ko.extenders.simpleFormat = (target, formatFunction) => {
            if (!formatFunction)
                formatFunction = defaultFormatIdentifier;

            var result = ko.pureComputed({
                read: target,
                write: newValue => {
                    var current = target();
                    var valueToWrite = formatFunction(newValue);

                    if (valueToWrite !== current) {
                        target(valueToWrite);
                    } else {
                        if (newValue !== current) {
                            target.notifySubscribers(valueToWrite);
                        }
                    }
                }
            }).extend({ notify: 'always' });

            result(target());
            return result;
        };

        ko.extenders.syncWithIdentifier = (target, options: ISyncWithIdentifierOptions) => {
            var identifier = options.identifier;
            var formatFunction = options.formatFunction;

            if (!formatFunction)
                formatFunction = defaultFormatIdentifier;

            var result = ko.pureComputed({
                read: target,
                write: newValue => {
                    var current = target();
                    var formatted = formatFunction(current);

                    if (newValue !== current) {
                        target(newValue);
                        if (formatted === ko.unwrap(identifier)) {
                            identifier(newValue);
                        }
                    }
                }
            }).extend({ notify: 'always' });

            result(target());
            return result;
        };

        ko.extenders.registerError = (target, options) => {
            var targetId = _.uniqueId();

            ko.computed(() => {
                var error = target.error();
                var errorList = (<KnockoutObservableArray<IExtendedErrorRegistrationItem>>options.errorList);
                var currentErrors = errorList.peek();

                if (error === null) {
                    errorList.remove(x => { return x.id === targetId; });
                } else {
                    var index = _.findIndex(currentErrors, x => { return x.id === targetId; });

                    var err: IExtendedErrorRegistrationItem = { id: targetId, friendlyName: options.friendlyName, error: error };
                    if (index === -1) {
                        errorList.push(err);
                    } else {
                        errorList.splice(index, 1, err);
                    }
                }
            });

            return target;
        };

        ko.extenders.validate = function (target, options) {
            var validations = options || {};
            var obs = target as any;
            _.each(validations, (value: any, key: string) => obs[key] = ko.pureComputed(value));
            obs.invalid = ko.pureComputed(() => _.reduce(validations, (memo, val, key) => memo || !obs[key](), false));
            obs.dirty = ko.observable(false);
            obs.validationError = ko.pureComputed(() => obs.dirty() && obs.invalid());
            obs.touch = () => obs.dirty(true);
            obs.reset = () => obs.dirty(false);
            return target as KnockoutValidatable<any>;
        };
    }
}